import { useState, useEffect, useCallback } from "react";
import { useDebounce } from "@/hooks/use-debounce";
import { useCustomers } from "@/services/customer-service";
import { Customer } from "@/types/customer-type";

const PAGE_SIZE = 20;

interface UseCustomerSearchOptions {
  searchTerm: string;
  isOpen: boolean;
}

interface UseCustomerSearchReturn {
  allCustomers: Customer[];
  isLoading: boolean;
  isLast: boolean;
  currentPage: number;
  isLoadingMore: boolean;
  handleLoadMore: () => void;
  reset: () => void;
}

export function useCustomerSearch({
  searchTerm,
  isOpen,
}: UseCustomerSearchOptions): UseCustomerSearchReturn {
  const [currentPage, setCurrentPage] = useState(0);
  const [allCustomers, setAllCustomers] = useState<Customer[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Reset to page 0 when search term changes
  useEffect(() => {
    if (isOpen) {
      setCurrentPage(0);
      setAllCustomers([]);
    }
  }, [debouncedSearchTerm, isOpen]);

  // Fetch customers with pagination
  const {
    items: customers,
    isLoading: isLoadingCustomers,
    isLast,
  } = useCustomers({
    page: currentPage,
    size: PAGE_SIZE,
    searchTerm: debouncedSearchTerm || undefined,
  });

  // Accumulate customers for pagination
  useEffect(() => {
    if (!isOpen) return;

    if (customers.length > 0) {
      if (currentPage === 0) {
        setAllCustomers(customers);
      } else {
        setAllCustomers((prev) => {
          const existingIds = new Set(prev.map((c) => String(c.id)));
          const newCustomers = customers.filter(
            (c) => !existingIds.has(String(c.id))
          );
          return [...prev, ...newCustomers];
        });
      }
    } else if (currentPage === 0 && !isLoadingCustomers) {
      setAllCustomers([]);
    }
  }, [customers, currentPage, isLoadingCustomers, isOpen]);

  const handleLoadMore = useCallback(() => {
    if (isLast || isLoadingCustomers || isLoadingMore) return;
    setIsLoadingMore(true);
    setCurrentPage((prev) => prev + 1);
    // Reset loading state after a short delay to allow the hook to fetch
    setTimeout(() => setIsLoadingMore(false), 500);
  }, [isLast, isLoadingCustomers, isLoadingMore]);

  const reset = useCallback(() => {
    setCurrentPage(0);
    setAllCustomers([]);
    setIsLoadingMore(false);
  }, []);

  return {
    allCustomers,
    isLoading: isLoadingCustomers,
    isLast,
    currentPage,
    isLoadingMore,
    handleLoadMore,
    reset,
  };
}
